import SpinnerOverlay from '@/components/SpinnerOverlay/SpinnerOverlay';
import { FlyerType } from '@/config/constants';
import FlyersContext from '@/context/FlyersContext';
import {
  BodyMedium,
} from '@/design-system';
import { GET_UPCOMING_FLYERS } from '@/graphql/queries';
import { flyerboardFlyersAmountVar, subscriptionsVar } from '@/graphql/state';
import { Flyer, GetUpcomingFlyersResponse } from '@/graphql/types';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import { sortBy } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useLocation } from 'react-router-dom';
import { Upcoming } from './Upcoming';
import styles from './Upcoming.styles';

const STICK_HEADER_HEIGHT = 163;
const PAGE_SIZE = 10;

export const UpcomingContainer: React.FC = () => {
  const { activeTab, onChangeFlyerType } =
    useContext(FlyersContext);

  const [getUpcomingFlyers] = useLazyQuery<GetUpcomingFlyersResponse>(GET_UPCOMING_FLYERS);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const { track } = useAnalytics();
  const [stickyTop, setStickyTop] = useState(STICK_HEADER_HEIGHT);
  const { search } = useLocation();
  const audienceId = new URLSearchParams(search).get('audienceId') || 'all';
  const [stickyHeaderHeight, setStickyHeaderHeight] =
    useState(STICK_HEADER_HEIGHT);
  const [filteredFlyers] = useState<Flyer[]>([]);
  const [flyers, setFlyers] = useState<Flyer[]>([]);
  const subscriptions = useReactiveVar(subscriptionsVar);
  const trackId = 'upcoming_page';
  const totalPages = useMemo(() => Math.ceil(total / PAGE_SIZE), [total]);

  useEffect(() => {
    const headerFixed = document.getElementById('headerFixed');
    if (headerFixed) {
      setStickyHeaderHeight(headerFixed.offsetHeight);
    }
  }, []);

  const selectedSubscriptions = useMemo(
    () =>
      sortBy(
        subscriptions.filter(subscription => subscription.type === activeTab),
        subscription => subscription.name,
      ),
    [subscriptions, activeTab],
  );

  const fetchData = useCallback(
    async () => {
      if (totalPages > 0 && page > totalPages) {
        return;
      }

      try {
        setLoading(true);
        const filters = audienceId && audienceId !== 'all'
          ? { schoolId: Number(audienceId) } : {};

        const { data } = await getUpcomingFlyers({
          variables: {
            input: {
              filters,
              pagination: {
                pageNumber: page,
                pageSize: PAGE_SIZE
              }
            }
          }
        });

        const { items, pagination } = data?.getUpcomingFlyers || { items: [], pagination: {} };
        const paginationTotal = pagination?.total || 0;
        if (items && items?.length > 0) {
          setFlyers((prevData) => [...prevData, ...items]);
        }

        if (paginationTotal > 0 && total === 0) {
          setTotal(paginationTotal);
          flyerboardFlyersAmountVar(paginationTotal);
        }
      } catch (err) {
        console.log(err);
      }
      finally {
        setLoading(false)
      }
    }, [page, totalPages, audienceId])

  useEffect(() => {
    fetchData();
  }, [page]);

  useEffect(() => {
    if (audienceId) {
      //clear the flyers when pick a school filter
      setFlyers([]);
      setTotal(0);
      if (page !== 1) {
        setPage(1);
      }
      else {
        fetchData();
      }
    }
  }, [audienceId])

  const nextPage = () => {
    setPage((prevPage) => prevPage + 1)
  }

  const onStickyTopChanged = (stickyTop: number) => {
    setStickyTop(stickyTop);
  };

  const handleChangeFlyerType = () => {
    onChangeFlyerType();
  };

  const onTabChangeFlyerType = (newFlyerType: string) => {
    if (newFlyerType) {
      handleChangeFlyerType();
    }
  };

  const handleClickFlyerType = () => {
    track({
      category: `${trackId}:view_upcoming_flyers`,
      action: GenericTrackTypes.BUTTON_PRESSED,
      label: FlyerType.school === activeTab ? 'Community' : 'School',
    });
    onChangeFlyerType();
  };

  const props = {
    trackId,
    flyerType: activeTab,
    stickyHeaderHeight,
    stickyTop,
    flyers: filteredFlyers,
    subscriptions: selectedSubscriptions,
    selectedAudienceId: audienceId,
    onTabChangeFlyerType,
    handleClickFlyerType,
    onStickyTopChanged,
  };

  if (loading) return <SpinnerOverlay loading />

  return (
    <InfiniteScroll
      dataLength={flyers.length}
      next={nextPage}
      hasMore={flyers.length < total}
      loader={<SpinnerOverlay loading />}
      endMessage={
        flyers.length > 0 && (<BodyMedium className={styles.endMessage}>
          No more flyers to load.
        </BodyMedium>)
      }
    >
      <Upcoming {...props} />
    </InfiniteScroll>
  )
};
